import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BingoBoard } from "../../components/bingo/BingoBoard";
import { wsService, checkAllBingos } from "../../services/websocket";
import { BingoCard, ObsSettings } from "../../types";
import { getCurrentObsSession } from "../../utils/storage";
import "./ObsView.css";

// Apply OBS settings to CSS variables
function applyObsSettings(settings: ObsSettings) {
  const root = document.documentElement;

  // Convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const bgColorWithOpacity = hexToRgba(
    settings.bgColor,
    settings.bgOpacity / 100,
  );
  root.style.setProperty("--obs-bg", bgColorWithOpacity);
  root.style.setProperty("--obs-text", settings.textColor);
  root.style.setProperty("--obs-border", settings.borderColor);
  root.style.setProperty("--obs-border-radius", `${settings.borderRadius}px`);
  root.style.setProperty("--obs-win", settings.winningColor);
  root.style.setProperty("--obs-font-size", `${settings.fontSize}px`);
  root.style.setProperty("--obs-font-weight", `${settings.fontWeight}`);
}

export const ObsView: React.FC = () => {
  const { code: urlLobbyCode, playerId: urlPlayerId } = useParams<{
    code: string;
    playerId: string;
  }>();
  const navigate = useNavigate();

  const [card, setCard] = useState<BingoCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasBingo, setHasBingo] = useState(false);
  const [bingoCount, setBingoCount] = useState(0);
  const [winningIndices, setWinningIndices] = useState<number[] | null>(null);

  // Use refs for active session to avoid stale closures in intervals
  const activeLobbyCodeRef = useRef<string | undefined>(urlLobbyCode);
  const activePlayerIdRef = useRef<string | undefined>(urlPlayerId);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);

  // Load and listen for OBS settings updates
  useEffect(() => {
    if (!urlLobbyCode) return;

    const loadSettings = async () => {
      try {
        // Ensure WebSocket is connected before loading settings
        await wsService.connect();

        const response = await wsService.getObsSettings({
          lobbyCode: urlLobbyCode,
        });
        applyObsSettings(response.settings);
      } catch (error) {
        console.error("Failed to load OBS settings:", error);
        // Apply default settings with black border and white background
        applyObsSettings({
          bgOpacity: 100,
          bgColor: "#ffffff",
          borderRadius: 0,
          textColor: "#000000",
          borderColor: "#000000",
          winningColor: "#4caf50",
          fontSize: 25,
          fontWeight: 800,
        });
      }
    };

    loadSettings();

    // Listen for settings updates
    const unsubscribe = wsService.subscribe((message) => {
      if (message.type === "obs_settings_update") {
        const payload = message.payload as { settings: ObsSettings };
        applyObsSettings(payload.settings);
      }
    });

    return unsubscribe;
  }, [urlLobbyCode]);

  // Initialize on mount
  useEffect(() => {
    activeLobbyCodeRef.current = urlLobbyCode;
    activePlayerIdRef.current = urlPlayerId;
  }, [urlLobbyCode, urlPlayerId]);

  // Main effect: connect, poll, and check for session changes
  useEffect(() => {
    if (!urlLobbyCode || !urlPlayerId) return;

    let isMounted = true;

    // Set initial refs
    activeLobbyCodeRef.current = urlLobbyCode;
    activePlayerIdRef.current = urlPlayerId;

    const fetchCard = async (lobbyCode: string, playerId: string) => {
      try {
        const response = await wsService.getPlayerCard({ lobbyCode, playerId });
        // Only update if this is still the active session
        if (
          isMounted &&
          activeLobbyCodeRef.current === lobbyCode &&
          activePlayerIdRef.current === playerId
        ) {
          setCard(response.card);
          setError(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Failed to get player card:", err);
        if (isMounted && activeLobbyCodeRef.current === lobbyCode) {
          setError(
            err instanceof Error ? err.message : "Failed to get player card",
          );
          setIsLoading(false);
        }
      }
    };

    const startPolling = () => {
      // Clear any existing interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // Poll every 500ms
      pollIntervalRef.current = setInterval(() => {
        // Check for session change first
        const session = getCurrentObsSession();
        if (
          session &&
          (session.lobbyCode !== activeLobbyCodeRef.current ||
            session.playerId !== activePlayerIdRef.current)
        ) {
          // Session changed - update refs immediately
          activeLobbyCodeRef.current = session.lobbyCode;
          activePlayerIdRef.current = session.playerId;

          // Reset state
          setCard(null);
          setIsLoading(true);
          setError(null);
          setHasBingo(false);
          setBingoCount(0);
          setWinningIndices(null);

          // Navigate to new URL
          navigate(`/obs/${session.lobbyCode}/${session.playerId}`, {
            replace: true,
          });

          // Fetch new card immediately
          fetchCard(session.lobbyCode, session.playerId);
        } else if (activeLobbyCodeRef.current && activePlayerIdRef.current) {
          // Normal poll - fetch current card
          fetchCard(activeLobbyCodeRef.current, activePlayerIdRef.current);
        }
      }, 500);
    };

    const init = async () => {
      try {
        await wsService.connect();
        await fetchCard(urlLobbyCode, urlPlayerId);
        startPolling();
        isInitializedRef.current = true;
      } catch (err) {
        console.error("Failed to connect:", err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Failed to connect");
          setIsLoading(false);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [urlLobbyCode, urlPlayerId, navigate]); // Re-run when URL params change

  // Check for bingo after card changes
  useEffect(() => {
    if (!card) return;

    const allBingos = checkAllBingos(card.cells, card.gridSize);
    const count = allBingos.length;
    const winningCells = allBingos.flat();

    if (count > 0 && !hasBingo) {
      setHasBingo(true);
    } else if (count === 0 && hasBingo) {
      setHasBingo(false);
    }

    if (count !== bingoCount) {
      setBingoCount(count);
    }

    setWinningIndices(winningCells.length > 0 ? winningCells : null);
  }, [card, hasBingo, bingoCount]);

  if (isLoading) {
    return (
      <div className="obs-view theme-light">
        <div className="obs-view__loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="obs-view theme-light">
        <div className="obs-view__error">{error}</div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="obs-view theme-light">
        <div className="obs-view__error">No card found</div>
      </div>
    );
  }

  return (
    <div className="obs-view theme-light">
      <BingoBoard
        gridSize={card.gridSize}
        cells={card.cells}
        onCellToggle={() => {}} // Read-only, no toggle
        winningIndices={winningIndices}
      />
    </div>
  );
};
